import { Flex, Spin } from "antd"
import React, { useEffect } from "react"
import { type ConnectedProps, connect } from "react-redux"

import { BREAKPOINTS } from "../../../constants/breakpoint.ts"
import { type RootState } from "../../../store.ts"
import { loadAccessibleBranches } from "../../Branches/redux/branches.slice.ts"
import { fetchSelfDeletionStatus } from "../redux/deletion-lock.thunks.ts"
import AccountDeletionLockPage from "./AccountDeletionLockPage.tsx"

const LOCKED_STATUSES = ["pending", "processing"]

const mapStateToProps = (state: RootState) => ({
  isAuthorized:
    !!state.auth?.accessToken && !!state.auth.userInfo && !state.auth.isAdmin,
  status: state.selfDeletion.status,
  fetched: state.selfDeletion.fetched,
  fetching: state.selfDeletion.fetching
})

const mapDispatchToProps = {
  fetchStatus: fetchSelfDeletionStatus,
  loadBranches: loadAccessibleBranches
}

const connector = connect(mapStateToProps, mapDispatchToProps)

interface DeletionLockGateProps extends ConnectedProps<typeof connector> {
  children: React.ReactNode
}

/**
 * Sits above CustomLayout so a locked user never sees the header or sidebar —
 * there is nothing to navigate away with.
 */
const DeletionLockGate = ({
  isAuthorized,
  status,
  fetched,
  fetching,
  fetchStatus,
  loadBranches,
  children
}: DeletionLockGateProps) => {
  useEffect(() => {
    if (isAuthorized && !fetched && !fetching) {
      fetchStatus()
    }
  }, [isAuthorized, fetched, fetching, fetchStatus])

  // The header's branch selector reads the account's branches once the app
  // shows; started here, alongside the status, they are usually known before
  // the badges and Home ask for anything scoped to the active branch. Only
  // where that selector shows: a phone keeps the scope it had.
  useEffect(() => {
    if (isAuthorized && window.innerWidth > BREAKPOINTS.MD) loadBranches()
  }, [isAuthorized, loadBranches])

  // Unauthenticated: let PrivateRoute do the redirecting.
  if (!isAuthorized) {
    return <>{children}</>
  }

  // Never render the app before the status is known — it would flash and then
  // yank a locked user out of a page they already started reading.
  if (!fetched) {
    return (
      <Flex align="center" justify="center" className="min-h-svh w-full">
        <Spin size="large" />
      </Flex>
    )
  }

  if (status && LOCKED_STATUSES.includes(status)) {
    return <AccountDeletionLockPage />
  }

  return <>{children}</>
}

export default connector(DeletionLockGate)
